/**
 * Niet in het origineel: de eisen aan het wachtwoord van een spelleider, gedeeld tussen
 * client en worker. Zo laat het scherm precies zien wat de server afdwingt.
 *
 * Overgenomen uit VIP (`shared/src/domain/wachtwoord.ts`), dat de OWASP Authentication
 * Cheat Sheet en ASVS 5.0 V6.2 volgt:
 * - minimaal 15 tekens, want er is geen tweede factor;
 * - geen samenstellingsregels: vier woorden achter elkaar is sterker dan `Welkom2026!`;
 * - alles mag, ook spaties, zodat een wachtwoordzin kan;
 * - maximaal 64 tekens en 72 bytes, omdat bcrypt de rest negeert.
 *
 * De controle op uitgelekte wachtwoorden vraagt een netwerkverzoek en staat in de worker.
 */
export const WACHTWOORD_MIN_LENGTE = 15;
export const WACHTWOORD_MAX_LENGTE = 64;
export const WACHTWOORD_MAX_BYTES = 72;
/** Woorden die in deze context niets toevoegen: wie weet waar hij inbreekt, raadt ze. */
const VERBODEN_WOORDEN = [
    'veerenstael',
    'koekjes',
    'stammetjes',
    'spelleider',
    'wachtwoord',
    'password',
    'welkom',
    'welcome',
    'geheim',
    'qwerty',
    'letmein',
];
/** Aantal UTF-8-bytes; `for...of` loopt over code points, dus een emoji telt als vier. */
function byteLengte(waarde) {
    let bytes = 0;
    for (const teken of waarde) {
        const cp = teken.codePointAt(0) ?? 0;
        bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
}
/** Cijfers en tekens die op letters lijken eruit, zodat `V33r3nst@el` ook opvalt. */
function vereenvoudig(waarde) {
    return waarde
        .toLowerCase()
        .replace(/[4@]/g, 'a')
        .replace(/[3]/g, 'e')
        .replace(/[1!|]/g, 'i')
        .replace(/[0]/g, 'o')
        .replace(/[5$]/g, 's')
        .replace(/[7]/g, 't')
        .replace(/[^a-z]/g, '');
}
/**
 * Alle bezwaren tegen een wachtwoord tegelijk, niet alleen het eerste: zo hoeft niemand te
 * gokken tot het goed is. Een lege lijst betekent: in orde.
 */
export function keurWachtwoord(wachtwoord, context = {}) {
    const fouten = [];
    if (wachtwoord.length < WACHTWOORD_MIN_LENGTE) {
        fouten.push(`Gebruik minstens ${WACHTWOORD_MIN_LENGTE} tekens.`);
    }
    if (wachtwoord.length > WACHTWOORD_MAX_LENGTE) {
        fouten.push(`Gebruik hooguit ${WACHTWOORD_MAX_LENGTE} tekens.`);
    }
    else if (byteLengte(wachtwoord) > WACHTWOORD_MAX_BYTES) {
        fouten.push('Dit wachtwoord is te lang door speciale tekens. Maak het wat korter.');
    }
    const kaal = vereenvoudig(wachtwoord);
    const lokaalDeel = context.email?.split('@')[0] ?? '';
    const eigenWoorden = [lokaalDeel, ...(context.naam ?? '').split(/\s+/)]
        .map(vereenvoudig)
        .filter((woord) => woord.length >= 4);
    if (eigenWoorden.some((woord) => kaal.includes(woord))) {
        fouten.push('Gebruik niet je naam of je e-mailadres in het wachtwoord.');
    }
    if (VERBODEN_WOORDEN.some((woord) => kaal.includes(woord))) {
        fouten.push('Dit wachtwoord bevat een te voorspelbaar woord.');
    }
    // Een kort stukje dat zich herhaalt is raadbaar, hoe lang het geheel ook is.
    if (/^(.{1,4})\1+$/.test(wachtwoord)) {
        fouten.push('Dit wachtwoord is een herhaling van hetzelfde stukje.');
    }
    return fouten;
}
